import React from 'react'
import { useTar } from '@/modules'
import { groupTemplates, type GroupKey } from '@/hooks/useChallengeBrowser'

const CARDS_PER_CATEGORY = 4

// A single placeholder shaped like the redesigned ChallengeCard: cover band on
// top, then title / teaser / footer bars.
const CardSkeleton = () => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
    <div className="h-28 animate-pulse bg-muted/40" />
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="h-5 w-3/4 animate-pulse rounded bg-muted/40" />
      <div className="h-4 w-full animate-pulse rounded bg-muted/25" />
      <div className="h-4 w-5/6 animate-pulse rounded bg-muted/25" />
      <div className="mt-2 flex items-center gap-2 pt-1">
        <div className="h-4 w-16 animate-pulse rounded bg-muted/25" />
        <div className="ms-auto h-9 w-28 animate-pulse rounded-md bg-muted/40" />
      </div>
    </div>
  </div>
)

// Loading state for the challenge grid: the page chrome renders instantly while
// each category (per the saved grouping) shows four card skeletons.
export const ChallengeGridSkeleton = ({ group }: { group: GroupKey }) => {
  const { t } = useTar()
  return (
    <div className="flex flex-col gap-12">
      {groupTemplates(group).map(({ key, labelKey, icon: Icon, iconClassName }) => (
        <section key={key} className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-border/70 bg-muted/50 text-foreground">
              <Icon className={`h-[18px] w-[18px] ${iconClassName ?? ''}`} />
            </span>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">{t(labelKey)}</h2>
            <span className="h-6 w-8 flex-none animate-pulse rounded-full bg-muted/50" />
            <div className="h-px flex-1 bg-border/50" />
          </div>
          <div className="grid justify-start gap-6 grid-cols-1 sm:[grid-template-columns:repeat(auto-fill,22rem)]">
            {Array.from({ length: CARDS_PER_CATEGORY }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
